import { Component, inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { ActiveModal } from '@rocketfinancialcorp/rocket-ui/modal';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { formDatepicker, formInput, formRow, formTextarea, formTimepicker, RktFormModule } from '@rocketfinancialcorp/rocket-ui/form';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { FundsOnHoldService } from '@shared/services';
import { ErrorUtils } from '@shared/utils';
import { FormModel } from '@shared/models';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-create-fund-on-hold',
  templateUrl: './create-fund-on-hold.component.html',
  imports: [FormsModule, ReactiveFormsModule, RktFormModule, RktButtonDirective, NgxMaskDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [provideNgxMask({ thousandSeparator: ',', decimalMarker: '.' })],
})
export class CreateFundOnHoldComponent implements OnDestroy {
  activeModal = inject(ActiveModal);
  private fundsOnHoldService = inject(FundsOnHoldService);

  financialAccountId = '';

  createForm = new FormGroup({});

  amountField = new FormControl();

  amountValue = '';

  amountTouched = false;

  isLoading = false;

  formSubmitError = '';

  createFields = [
    formRow([
      formDatepicker({
        key: 'expiryDate',
        label: 'Expiry Date',
        props: {
          required: true,
          placeholder: 'MM/DD/YY',
          minDate: NgbDate.from({ year: new Date().getFullYear(), month: new Date().getMonth() + 1, day: new Date().getDate() }),
          maxDate: NgbDate.from({ year: 2099, month: 12, day: 31 }),
        },
        validation: {
          messages: {
            required: 'Expiry date is required',
          },
        },
      }),
      formTimepicker({
        key: 'expiryTime',
        label: 'Expiry Time',
        props: {
          required: true,
        },
        validation: {
          messages: {
            required: 'Expiry time is required',
          },
        },
      }),
    ]),
    formRow([
      formTextarea({
        key: 'memo',
        label: 'Memo',
        props: {
          maxLength: 500,
          rows: 3,
        },
      }),
    ]),
    formRow([
      formInput({
        key: 'externalReferenceId',
        label: 'External Reference ID',
      }),
    ]),
  ];

  formModel: FormModel<{
    expiryDate: string;
    expiryTime: string;
    memo: string;
    externalReferenceId: string;
  }> = {};

  private destroy$ = new Subject<void>();

  private toISODate(dateStr: string): string {
    if (dateStr.includes('/')) {
      const [month, day, year] = dateStr.split('/');
      const fullYear = year.length === 2 ? `20${year}` : year;
      return `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateStr;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get parsedAmount(): number {
    if (!this.amountValue) {
      return 0;
    }
    return parseFloat(String(this.amountValue).replace(/,/g, '')) || 0;
  }

  get isAmountInvalid(): boolean {
    return this.amountTouched && this.parsedAmount <= 0;
  }

  get isFormInvalid(): boolean {
    return this.createForm.invalid || this.parsedAmount <= 0;
  }

  onAmountChange(value: string): void {
    this.amountValue = value;
  }

  onAmountBlur(): void {
    this.amountTouched = true;
  }

  onSubmit(): void {
    this.createForm.markAllAsTouched();
    this.amountTouched = true;

    if (this.isFormInvalid) {
      return;
    }

    this.isLoading = true;
    this.formSubmitError = '';

    const { expiryDate, expiryTime, memo, externalReferenceId } = this.formModel;

    // Convert MM/DD/YYYY date from datepicker to ISO 8601 format
    const isoDate = this.toISODate(expiryDate!);
    const expiry = expiryTime ? `${isoDate}T${expiryTime}Z` : `${isoDate}T00:00:00Z`;

    if (new Date(expiry) <= new Date()) {
      this.isLoading = false;
      this.formSubmitError = 'Expiry must be a future date and time.';
      return;
    }

    this.fundsOnHoldService
      .createFundOnHold({
        financialAccountId: this.financialAccountId,
        expiry,
        amount: this.parsedAmount,
        ...(memo ? { memo } : {}),
        ...(externalReferenceId ? { externalReferenceId } : {}),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.activeModal.close(response);
          this.createForm.reset();
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this.formSubmitError = ErrorUtils.parseExtendedError(error);
        },
      });
  }
}
